import { Button } from "../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectGroup, SelectLabel, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Avatar, AvatarImage, AvatarFallback } from "../../components/ui/avatar";
import { Mic, AlertTriangle, Globe, Wifi } from "lucide-react";
import { CATEGORIES } from "../../constants/scenarioCategories";

function groupByCategory(scenarios) {
  const groups = {};
  for (const s of scenarios) {
    const cat = s.category || "other";
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(s);
  }
  return groups;
}

export function SetupForm({
  scenarios, personas, selectedScenario, setSelectedScenario,
  selectedPersona, setSelectedPersona, selectedLanguage, setSelectedLanguage,
  micError, requestMicPermission, onStartPractice
}) {
  const currentScenario = scenarios.find(s => s.id === selectedScenario);

  return (
    <div className="max-w-2xl mx-auto fade-up">
      <Card className="premium-card shadow-xl border-slate-200/60">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 bg-[#0B1F3B] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Mic className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold text-[#0B1F3B]">Start Voice Practice</CardTitle>
          <CardDescription className="text-slate-500">Configure your session and begin speaking</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {micError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl" data-testid="mic-error-alert">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-red-700">Microphone Error</h4>
                  <p className="text-sm text-red-600">{micError}</p>
                  <Button variant="outline" size="sm" className="mt-2" onClick={requestMicPermission}>Try Again</Button>
                </div>
              </div>
            </div>
          )}

          {/* Language */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#0B1F3B] flex items-center gap-2">
              <Globe className="w-4 h-4 text-[#2563EB]" /> Select Language
            </label>
            <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
              <SelectTrigger data-testid="language-select" className="border-slate-200 focus:border-[#2563EB] focus:ring-[#2563EB]">
                <SelectValue placeholder="Select language" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hinglish">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Hinglish</span>
                    <Badge variant="outline" className="text-xs bg-[#14B8A6]/10 text-[#14B8A6] border-[#14B8A6]/20">Default</Badge>
                  </div>
                </SelectItem>
                <SelectItem value="en"><span>English</span></SelectItem>
                <SelectItem value="hi"><span>Hindi</span></SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-400">
              {selectedLanguage === 'hinglish' ? 'Natural mix of Hindi + English business terms (policy, premium, etc.)' : selectedLanguage === 'hi' ? 'Customer and feedback in Hindi' : 'Customer responses and feedback in English'}
            </p>
          </div>

          {/* Scenario */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#0B1F3B]">Scenario</label>
            <Select value={selectedScenario} onValueChange={setSelectedScenario}>
              <SelectTrigger data-testid="scenario-select" className="border-slate-200 focus:border-[#2563EB] focus:ring-[#2563EB]">
                <SelectValue placeholder="Select a scenario" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(groupByCategory(scenarios)).map(([catId, catScenarios]) => (
                  <SelectGroup key={catId}>
                    <SelectLabel className="text-xs text-slate-400 uppercase tracking-wider">{CATEGORIES[catId] || catId}</SelectLabel>
                    {catScenarios.map((scenario) => (
                      <SelectItem key={scenario.id} value={scenario.id}>
                        <div className="flex items-center gap-2">
                          <span>{selectedLanguage === 'hi' ? scenario.name_hi : scenario.name}</span>
                          <Badge variant="outline" className="text-xs">{scenario.difficulty}</Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
            {currentScenario && (
              <p className="text-sm text-slate-400 mt-2">
                {selectedLanguage === 'hi' ? currentScenario.description_hi : currentScenario.description}
              </p>
            )}
          </div>

          {/* Persona */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#0B1F3B]">Customer Persona</label>
            <Select value={selectedPersona} onValueChange={setSelectedPersona}>
              <SelectTrigger data-testid="persona-select" className="border-slate-200 focus:border-[#2563EB] focus:ring-[#2563EB]">
                <SelectValue placeholder="Select a persona" />
              </SelectTrigger>
              <SelectContent>
                {personas.map((persona) => (
                  <SelectItem key={persona.id} value={persona.id}>
                    <div className="flex items-center gap-3">
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={persona.avatar} />
                        <AvatarFallback className="bg-[#0B1F3B] text-white text-xs">{persona.name[0]}</AvatarFallback>
                      </Avatar>
                      <div>
                        <span className="font-medium">{selectedLanguage === 'hi' ? persona.name_hi : persona.name}</span>
                        <span className="text-slate-400 text-xs ml-2">{selectedLanguage === 'hi' ? persona.occupation_hi : persona.occupation}</span>
                        {persona.voice_config && <span className="text-[#2563EB] text-xs ml-2">({persona.voice_config.voice_label})</span>}
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Voice Info */}
          <div className="p-4 bg-[#0B1F3B]/5 rounded-xl border border-[#0B1F3B]/10">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-[#14B8A6]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Wifi className="w-5 h-5 text-[#14B8A6]" />
              </div>
              <div>
                <h4 className="font-semibold text-[#0B1F3B] text-sm">Low-Latency Voice Streaming</h4>
                <p className="text-xs text-slate-500 mt-1">Real-time WebSocket streaming for instant AI responses. Speak naturally — no typing needed.</p>
              </div>
            </div>
          </div>

          <Button
            className="w-full bg-[#0B1F3B] hover:bg-[#0B1F3B]/90 h-14 text-lg rounded-xl btn-premium"
            onClick={onStartPractice}
            disabled={!selectedScenario}
            data-testid="start-roleplay-btn"
          >
            <Mic className="w-6 h-6 mr-2" /> Start Practice
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
